import React from 'react';
import { ArrowLeft, Shield, Heart, Baby, Users, CheckCircle, Umbrella, FileText, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';

const InsurancePage = () => {
  const insuranceTypes = [
    {
      icon: <Heart size={40} />,
      title: "Life Insurance",
      description: "Protect your family's financial future with comprehensive life insurance coverage",
      features: [
        "Term life insurance plans",
        "Whole life insurance",
        "ULIPs (Unit Linked Insurance Plans)",
        "Endowment plans",
        "Money-back policies"
      ],
      benefits: [
        "Financial security for family",
        "Tax benefits under Section 80C",
        "Wealth creation opportunities",
        "Loan facility against policy"
      ]
    },
    {
      icon: <Shield size={40} />,
      title: "Health Insurance",
      description: "Safeguard yourself and your family against rising medical costs",
      features: [
        "Individual health insurance",
        "Family floater plans",
        "Critical illness coverage",
        "Personal accident insurance",
        "Senior citizen health plans"
      ],
      benefits: [
        "Cashless treatment facility",
        "Tax deduction under Section 80D",
        "Coverage for pre and post hospitalization",
        "Annual health check-ups"
      ]
    },
    {
      icon: <Baby size={40} />,
      title: "Child Insurance",
      description: "Secure your child's education and future with specialized child insurance plans",
      features: [
        "Child education plans",
        "Child marriage plans",
        "Child ULIPs",
        "Term insurance for children",
        "Scholarship benefits"
      ],
      benefits: [
        "Guaranteed education fund",
        "Waiver of premium benefit",
        "Maturity benefits",
        "Life coverage for child"
      ]
    }
  ];

  const whyInsurance = [
    {
      title: "Rising Healthcare Costs",
      description: "Medical inflation is rising at 15% annually. Insurance provides protection against unexpected medical expenses."
    },
    {
      title: "Income Replacement",
      description: "Life insurance ensures your family's lifestyle is maintained even in your absence."
    },
    {
      title: "Tax Benefits",
      description: "Get tax deductions under various sections of Income Tax Act while securing your future."
    },
    {
      title: "Peace of Mind",
      description: "Financial security allows you to focus on your goals without worrying about uncertainties."
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "Assess Your Needs",
      description: "Evaluate your current financial situation, dependents, and future goals to determine insurance requirements."
    },
    {
      step: "2",
      title: "Choose Right Plan",
      description: "Select appropriate insurance plans based on your age, income, health status, and financial objectives."
    },
    {
      step: "3",
      title: "Complete Application",
      description: "Fill out the application form with accurate information and submit required documents."
    },
    {
      step: "4",
      title: "Medical Examination",
      description: "Undergo medical tests if required. Many plans offer hassle-free online applications."
    },
    {
      step: "5",
      title: "Policy Issuance",
      description: "Once approved, receive your policy documents and start enjoying comprehensive coverage."
    }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Breadcrumb Navigation */}
      <div className="bg-light-gold py-4">
        <div className="container">
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="text-primary-green hover:text-primary-gold transition-colors">
              Home
            </Link>
            <span className="text-medium-gray">/</span>
            <span className="text-primary-green font-medium">Insurance Services</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-hero">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Link 
                to="/" 
                className="btn-secondary inline-flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
            </div>
            
            <div className="text-primary-gold mb-4">
              <Umbrella size={64} className="mx-auto" />
            </div>
            
            <h1 className="heading-hero mb-6">
              Insurance <span className="text-primary-gold">Services</span>
            </h1>
            
            <p className="text-large mb-8 max-w-3xl mx-auto">
              Protect what matters most with comprehensive insurance solutions for life, health, and your child's future.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={scrollToContact}
                className="btn-primary"
              >
                <Shield size={20} />
                Get Insurance Quote
              </button>
              <button 
                onClick={() => document.getElementById('insurance-types').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
              >
                Explore Plans
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Insurance is Important */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Why Insurance is Essential</h2>
            <p className="text-large max-w-3xl mx-auto">
              In today's uncertain world, insurance isn't just an option—it's a necessity for financial security
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {whyInsurance.map((reason, index) => (
              <div key={index} className="bg-cream-bg rounded-3xl p-8 hover:shadow-xl transition-all duration-300 fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <h3 className="heading-card mb-4">{reason.title}</h3>
                <p className="text-body">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Types */}
      <section id="insurance-types" className="section-padding bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Our Insurance Solutions</h2>
            <p className="text-large max-w-3xl mx-auto">
              Comprehensive insurance products designed to protect every aspect of your financial life
            </p>
          </div>
          
          <div className="space-y-12">
            {insuranceTypes.map((insurance, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={`fade-in-up ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="text-primary-gold mb-6">
                    {insurance.icon}
                  </div>
                  <h3 className="heading-section mb-6">{insurance.title}</h3>
                  <p className="text-body mb-8">{insurance.description}</p>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-poppins font-semibold text-primary-green mb-4">Plan Types:</h4>
                      <ul className="space-y-3">
                        {insurance.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start gap-3">
                            <CheckCircle size={16} className="text-primary-gold mt-1 flex-shrink-0" />
                            <span className="text-body text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className={`fade-in-up delay-2 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="bg-white rounded-3xl p-8 shadow-xl">
                    <h4 className="heading-card mb-6">Key Benefits</h4>
                    <ul className="space-y-4">
                      {insurance.benefits.map((benefit, benefitIndex) => (
                        <li key={benefitIndex} className="flex items-start gap-3">
                          <div className="w-3 h-3 bg-primary-gold rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-body">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Process */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Simple Insurance Process</h2>
            <p className="text-large max-w-3xl mx-auto">
              Getting insured is easier than you think. Follow our streamlined process for quick coverage.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {processSteps.map((process, index) => (
                <div key={index} className={`grid md:grid-cols-12 gap-8 items-center fade-in-up ${index % 2 === 1 ? 'md:direction-rtl' : ''}`} style={{animationDelay: `${index * 0.2}s`}}>
                  <div className={`md:col-span-3 ${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    <div className="w-24 h-24 bg-primary-gold rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto">
                      {process.step}
                    </div>
                  </div>
                  <div className={`md:col-span-9 ${index % 2 === 1 ? 'md:order-1 md:text-right' : ''}`}>
                    <h3 className="heading-card mb-4">{process.title}</h3>
                    <p className="text-body">{process.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tax Benefits Section */}
      <section className="section-padding bg-primary-green text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-poppins font-semibold mb-6">Insurance Tax Benefits</h2>
            <p className="text-lg mb-12 opacity-90">
              Maximize your tax savings while securing your financial future with insurance policies
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl mb-4">📋</div>
                <h3 className="font-poppins font-semibold mb-3">Section 80C</h3>
                <p className="text-sm opacity-80 mb-2">Life Insurance Premiums</p>
                <p className="text-primary-gold font-bold">Up to ₹1.5 Lakhs</p>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">🏥</div>
                <h3 className="font-poppins font-semibold mb-3">Section 80D</h3>
                <p className="text-sm opacity-80 mb-2">Health Insurance Premiums</p>
                <p className="text-primary-gold font-bold">Up to ₹75,000</p>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">💰</div>
                <h3 className="font-poppins font-semibold mb-3">Section 10(10D)</h3>
                <p className="text-sm opacity-80 mb-2">Maturity/Death Benefits</p>
                <p className="text-primary-gold font-bold">Tax-Free*</p>
              </div>
            </div>
            
            <p className="text-xs opacity-60 mt-8">*Subject to conditions as per Income Tax Act</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="section-padding bg-light-gold">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-section mb-6">Ready to Secure Your Future?</h2>
            <p className="text-large mb-8">
              Get personalized insurance recommendations based on your life stage, income, and protection needs.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <FileText size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Free Consultation</h3>
                <p className="text-body text-sm">Expert advice on insurance planning</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Calculator size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Coverage Calculator</h3>
                <p className="text-body text-sm">Determine optimal insurance coverage</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Users size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Family Plans</h3>
                <p className="text-body text-sm">Comprehensive family protection solutions</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+919904449922" className="btn-primary">
                Get Insurance Quote
              </a>
              <a href="mailto:nitumf2017@gmail.com" className="btn-secondary">
                Request Callback
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;